import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const RULE_FIELDS = [
  ["kpi_high_threshold", "High tier — KPI threshold (%)"],
  ["kpi_high_pct", "High tier — incentive (% of basic)"],
  ["kpi_mid_threshold", "Mid tier — KPI threshold (%)"],
  ["kpi_mid_pct", "Mid tier — incentive (% of basic)"],
  ["cert_bonus_qar", "Bonus per certification (QAR)"],
  ["dept_multiplier_threshold", "Dept target-completion threshold (%)"],
  ["dept_multiplier", "Dept multiplier (×)"],
];

export const PayrollSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState({});
  const [pensionRate, setPensionRate] = useState("5");
  const [pensionScope, setPensionScope] = useState("qatari");
  const [bands, setBands] = useState([]);
  const [saving, setSaving] = useState(false);
  const isAdmin = user.role === "admin";

  useEffect(() => {
    api.get("/payroll/settings").then((r) => {
      setSettings(r.data);
      setRules(r.data.incentive_rules);
      setPensionRate(String(r.data.deductions.pension_rate_pct));
      setPensionScope(r.data.deductions.pension_applies_to);
      setBands((r.data.increment_bands || []).map((b) => ({ ...b })));
    });
  }, []);

  if (!settings) return <Skeleton className="h-80 w-full rounded-none" />;

  const save = async () => {
    setSaving(true);
    try {
      const numeric = Object.fromEntries(Object.entries(rules).map(([k, v]) => [k, Number(v)]));
      const r = await api.put("/payroll/settings", {
        incentive_rules: numeric,
        deductions: { pension_rate_pct: Number(pensionRate), pension_applies_to: pensionScope },
        increment_bands: bands.map((b) => ({ ...b, min_score: Number(b.min_score), increment_pct: Number(b.increment_pct) })),
      });
      setSettings(r.data);
      toast.success("Payroll settings updated — recompute draft runs to apply");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5" data-testid="payroll-settings">
      <div className="xl:col-span-2 bg-white border border-border">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)]">Incentive Rules</p>
          <p className="text-[11px] text-muted-foreground">
            Updated {String(settings.updated_at).slice(0, 10)} by {settings.updated_by}
          </p>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {RULE_FIELDS.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <span className="text-[12px] text-slate-600">{label}</span>
              <Input data-testid={`setting-${key}`} type="number" step="any" disabled={!isAdmin}
                value={rules[key] ?? ""} onChange={(e) => setRules({ ...rules, [key]: e.target.value })}
                className="rounded-none w-28 h-8 text-[13px] font-mono text-right" />
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <div className="flex items-center justify-between gap-4">
            <span className="text-[12px] text-slate-600">Pension rate (% of basic)</span>
            <Input data-testid="setting-pension-rate" type="number" step="any" disabled={!isAdmin}
              value={pensionRate} onChange={(e) => setPensionRate(e.target.value)}
              className="rounded-none w-28 h-8 text-[13px] font-mono text-right" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[12px] text-slate-600">Pension eligibility</span>
            <Select value={pensionScope} onValueChange={setPensionScope} disabled={!isAdmin}>
              <SelectTrigger className="rounded-none w-40 h-8 text-[12px]" data-testid="setting-pension-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="qatari" className="text-[12px]">Qatari nationals</SelectItem>
                <SelectItem value="none" className="text-[12px]">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
            Increment bands (KPI Matrix weighted score → annual increment)
          </p>
          <div className="space-y-2" data-testid="increment-bands-editor">
            {bands.map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-[12px] text-slate-600 w-24">Score ≥</span>
                <Input data-testid={`band-min-${i}`} type="number" disabled={!isAdmin} value={b.min_score}
                  onChange={(e) => setBands(bands.map((x, j) => j === i ? { ...x, min_score: e.target.value } : x))}
                  className="rounded-none w-20 h-8 text-[13px] font-mono text-right" />
                <span className="text-[12px] text-slate-600">→ increment</span>
                <Input data-testid={`band-pct-${i}`} type="number" step="0.5" disabled={!isAdmin} value={b.increment_pct}
                  onChange={(e) => setBands(bands.map((x, j) => j === i ? { ...x, increment_pct: e.target.value } : x))}
                  className="rounded-none w-20 h-8 text-[13px] font-mono text-right" />
                <span className="text-[12px] text-slate-600">%</span>
                {b.development_plan && (
                  <span className="text-[10px] font-mono bg-amber-100 border border-amber-300 text-amber-900 px-1.5 py-0.5">
                    + DEVELOPMENT PLAN
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">No income tax applies (Qatar).</p>
          {isAdmin ? (
            <Button onClick={save} disabled={saving} data-testid="settings-save-btn"
              className="rounded-none bg-teal-600 hover:bg-teal-700 h-9 text-[12px]">
              {saving ? "Saving…" : "Save settings"}
            </Button>
          ) : (
            <p className="text-[11px] text-muted-foreground">Editing restricted to Administrators.</p>
          )}
        </div>
      </div>

      <div className="space-y-5">
        <div className="bg-white border border-border p-5" data-testid="kpi-linkage-card">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-teal-700 mb-2">
            Performance → Payroll linkage
          </p>
          <p className="text-[12.5px] text-slate-700 leading-relaxed">{settings.kpi_linkage}</p>
        </div>
        <div className="bg-[var(--brand-primary)] text-white p-5" data-testid="incentive-example-card">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-teal-400 mb-3">Worked example</p>
          <div className="space-y-1.5 text-[12px] font-mono text-slate-300">
            <p>Basic QAR 20,000 · KPI 92% · 1 cert</p>
            <p>Dept target completion 88% (≥ {rules.dept_multiplier_threshold}%)</p>
            <p className="border-t border-white/10 pt-1.5">Tier: 92 ≥ {rules.kpi_high_threshold} → {rules.kpi_high_pct}% = 2,000</p>
            <p>Certs: 1 × {Number(rules.cert_bonus_qar).toLocaleString()} = {Number(rules.cert_bonus_qar).toLocaleString()}</p>
            <p>Multiplier: × {rules.dept_multiplier}</p>
            <p className="text-teal-400 font-bold border-t border-white/10 pt-1.5">
              Incentive = (2,000 + {Number(rules.cert_bonus_qar).toLocaleString()}) × {rules.dept_multiplier} = QAR{" "}
              {Math.round((20000 * (rules.kpi_high_pct / 100) + Number(rules.cert_bonus_qar)) * rules.dept_multiplier).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollSettings;
